import { RESERVED_SLUG_RE } from '@planuze/admin-contracts';
import { Button, InlineAlert, Input, Spinner, toast } from '@planuze/ui';
import { XIcon } from 'lucide-react';
import { useState } from 'react';
import { useAdminLocale } from '../../content/locale-provider.js';
import { usePublishersContent } from '../../content/sections/publishers.js';
import { useReservedSlugs, useUpdateReservedSlugs } from '../../hooks/use-reserved-slugs.js';
import { configServiceErrorMessage } from '../../lib/config-service-error.js';

/* Painel de gestão de slugs reservados (ADR-0484, super_admin). Lista LIVE do config-service;
   add/remove faz PUT da lista inteira (replace). Alimenta também o Select do provision. */
export function ReservedSlugsCard() {
  const copy = usePublishersContent();
  const { locale } = useAdminLocale();
  const { data, isLoading, isError } = useReservedSlugs();
  const update = useUpdateReservedSlugs();
  const [draft, setDraft] = useState('');

  const slugs = data ?? [];
  const busy = update.isPending;

  const commit = (next: readonly string[]): void => {
    update.mutate(
      { slugs: [...next] },
      {
        onError: (err) =>
          toast.error(configServiceErrorMessage(err, copy.reservedSlugs.saveError, locale)),
      },
    );
  };

  const add = (): void => {
    const s = draft.trim().toLowerCase();
    if (s.length === 0) return;
    if (!RESERVED_SLUG_RE.test(s)) {
      toast.error(copy.reservedSlugs.invalidSlug);
      return;
    }
    if (slugs.includes(s)) {
      toast.error(copy.reservedSlugs.duplicateSlug);
      return;
    }
    commit([...slugs, s]);
    setDraft('');
  };

  const remove = (slug: string): void => commit(slugs.filter((s) => s !== slug));

  return (
    <section className="mb-4 flex flex-col gap-3 rounded-md border border-border p-4">
      <div className="flex flex-col gap-1">
        <h2 className="font-semibold text-base text-foreground">{copy.reservedSlugs.title}</h2>
        <p className="text-muted-foreground text-sm">{copy.reservedSlugs.description}</p>
      </div>

      {isError ? (
        <InlineAlert variant="destructive">{copy.reservedSlugs.loadError}</InlineAlert>
      ) : isLoading ? (
        <div className="grid place-items-center p-4">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {slugs.length === 0 ? (
            <span className="text-muted-foreground text-sm">{copy.reservedSlugs.empty}</span>
          ) : (
            slugs.map((slug) => (
              <span
                key={slug}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-1 font-mono text-foreground text-xs"
              >
                {slug}
                <button
                  type="button"
                  aria-label={copy.reservedSlugs.removeAriaLabel(slug)}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                  disabled={busy}
                  onClick={() => remove(slug)}
                >
                  <XIcon className="size-3" />
                </button>
              </span>
            ))
          )}
        </div>
      )}

      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <Input
          value={draft}
          placeholder={copy.reservedSlugs.placeholder}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          className="max-w-xs"
        />
        <Button type="submit" variant="outline" size="sm" loading={busy} disabled={busy}>
          {copy.reservedSlugs.add}
        </Button>
      </form>
    </section>
  );
}
